import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  CheckCircle2, XCircle, Clock, FileText, Shield, 
  User, Zap,
  Eye, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500', bgColor: 'bg-amber-50' },
  approved: { label: 'Approved', color: 'bg-emerald-500', bgColor: 'bg-emerald-50' },
  rejected: { label: 'Rejected', color: 'bg-red-500', bgColor: 'bg-red-50' },
};

const REQUEST_TYPE_LABELS = {
  quote_approval: 'Quote Approval',
  decision_override: 'Decision Override',
  capacity_override: 'Capacity Override',
};

export const ApprovalInboxPage = () => {
  const { user, authFetch } = useAuth();
  const _navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [decisionNote, setDecisionNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Enhanced filters
  const [filters, setFilters] = useState({
    status: 'all',
    request_type: 'all',
    requester_id: 'all',
    date_from: '',
    date_to: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    requesters: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0
  });
  
  // Related entity data
  const [entityData, setEntityData] = useState(null);

  const isDirector = user?.role === 'director';

  useEffect(() => {
    loadApprovals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, includeInactive]);

  const loadApprovals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.request_type !== 'all') params.append('request_type', filters.request_type);
      if (filters.requester_id !== 'all') params.append('requester_id', filters.requester_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (includeInactive) params.append('include_inactive', 'true');
      
      const res = await authFetch(`/approvals/filtered?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setApprovals(data.approvals || []);
        setPagination({
          page: data.page || 1,
          pages: data.pages || 1,
          total: data.total || 0
        });
        if (data.filters?.requesters) {
          setFilterOptions({ requesters: data.filters.requesters });
        }
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEntityData = async (approval) => {
    try {
      if (approval.entity_type === 'quote') {
        const res = await authFetch(`/quotes/${approval.entity_id}`);
        if (res.ok) {
          const data = await res.json();
          setEntityData({ type: 'quote', data });
        }
      } else if (approval.entity_type === 'decision_result') {
        const res = await authFetch(`/decisions/id/${approval.entity_id}`);
        if (res.ok) {
          const data = await res.json();
          setEntityData({ type: 'decision', data });
        }
      }
    } catch (err) {
      console.error('Load entity error:', err);
    }
  };

  const openReview = async (approval) => {
    setSelectedApproval(approval);
    setDecisionNote('');
    setEntityData(null);
    await loadEntityData(approval);
    setShowReviewModal(true);
  };

  const processApproval = async (action) => {
    if (!selectedApproval) return;
    
    if (action === 'reject' && decisionNote.length < 10) {
      toast.error('Decision note required (min 10 characters)');
      return;
    }
    
    if (action === 'approve' && decisionNote.length < 5) {
      toast.error('Please add a brief note for the approval');
      return;
    }
    
    setProcessing(true);
    try {
      const endpoint = action === 'approve' 
        ? `/approvals/${selectedApproval.id}/approve`
        : `/approvals/${selectedApproval.id}/reject`;
      
      const res = await authFetch(endpoint, {
        method: 'POST',
        body: { 
          reason: decisionNote,
          note: decisionNote 
        }
      });
      
      if (res.ok) {
        toast.success(action === 'approve' ? 'Request approved' : 'Request rejected');
        setShowReviewModal(false);
        setSelectedApproval(null);
        loadApprovals();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to process request');
      }
    } catch (err) {
      toast.error('Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      request_type: 'all',
      requester_id: 'all',
      date_from: '',
      date_to: '',
    });
  };

  const hasActiveFilters = filters.status !== 'all' || 
    filters.request_type !== 'all' || 
    filters.requester_id !== 'all' ||
    filters.date_from || 
    filters.date_to;

  const counts = {
    pending: approvals.filter(a => a.status === 'pending').length,
    approved: approvals.filter(a => a.status === 'approved').length,
    rejected: approvals.filter(a => a.status === 'rejected').length,
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="approval-inbox-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              Approval Inbox
            </h1>
            <p className="text-slate-500 mt-1">
              {isDirector 
                ? `${pagination.total} ${includeInactive ? 'request' : 'active request'}${pagination.total !== 1 ? 's' : ''}${includeInactive ? ' (including archived/deleted)' : ''}`
                : 'Track your approval requests'}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {isDirector && (
              <Button
                variant={includeInactive ? "default" : "outline"}
                size="sm"
                onClick={() => setIncludeInactive(!includeInactive)}
                className="rounded-full text-xs"
                data-testid="include-inactive-toggle"
              >
                {includeInactive ? 'Showing all' : 'Show archived'}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`rounded-full ${hasActiveFilters ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' : ''}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 px-2 py-0.5 bg-[#1e3a5f] text-white text-xs rounded-full">
                  Active
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm animate-fadeIn">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-[#1e3a5f]">Filter Requests</h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  Clear all
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="rounded-xl" data-testid="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Request Type Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">Request Type</Label>
                <Select
                  value={filters.request_type}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, request_type: value }))}
                >
                  <SelectTrigger className="rounded-xl" data-testid="filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="quote_approval">Quote Approval</SelectItem>
                    <SelectItem value="decision_override">Decision Override</SelectItem>
                    <SelectItem value="capacity_override">Capacity Override</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Requester Filter (Directors only) */}
              {isDirector && filterOptions.requesters.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1.5 block">Requested By</Label>
                  <Select
                    value={filters.requester_id}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, requester_id: value }))}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="filter-requester">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Team Members</SelectItem>
                      {filterOptions.requesters.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.first_name} {r.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Date Range */}
              <div className="lg:col-span-1">
                <Label className="text-xs text-slate-500 mb-1.5 block">Date Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    className="rounded-xl text-sm"
                    placeholder="From"
                    data-testid="filter-date-from"
                  />
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    className="rounded-xl text-sm"
                    placeholder="To"
                    data-testid="filter-date-to"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4">
          {['pending', 'approved', 'rejected'].map(status => {
            const config = STATUS_CONFIG[status];
            const isActive = filters.status === status;
            return (
              <button
                key={status}
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  status: prev.status === status ? 'all' : status 
                }))}
                className={`p-4 rounded-xl border transition-all ${
                  isActive 
                    ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid={`filter-${status}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${config.color}`} />
                  <span className="text-sm font-medium text-slate-600 capitalize">{status}</span>
                </div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{counts[status]}</p>
              </button>
            );
          })}
        </div>

        {/* Approvals List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-pulse text-slate-500">Loading...</div>
            </div>
          ) : approvals.length > 0 ? (
            approvals.map(approval => {
              const status = STATUS_CONFIG[approval.status] || STATUS_CONFIG.pending;
              
              return (
                <div
                  key={approval.id}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
                  data-testid={`approval-${approval.id}`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl ${status.bgColor} flex items-center justify-center`}>
                          {approval.status === 'pending' ? (
                            <Clock className="w-6 h-6 text-amber-600" />
                          ) : approval.status === 'approved' ? (
                            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                          ) : (
                            <XCircle className="w-6 h-6 text-red-600" />
                          )}
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`chip ${status.color} text-white text-xs`}>
                              {status.label}
                            </span>
                            <span className="text-sm text-slate-500">
                              {REQUEST_TYPE_LABELS[approval.request_type] || approval.request_type}
                            </span>
                          </div>
                          
                          <h3 className="font-heading font-semibold text-[#1e3a5f]">
                            {approval.entity_type === 'quote' && 'Quote Approval Request'}
                            {approval.entity_type === 'decision_result' && 'Decision Override Request'}
                          </h3>
                          
                          {/* Entity Summary */}
                          {approval.entity_summary && (
                            <div className="mt-2 px-3 py-2 bg-slate-50 rounded-lg inline-flex items-center gap-3 text-sm">
                              <span className="font-mono text-slate-600">
                                {approval.entity_summary.quote_number}
                              </span>
                              {approval.entity_summary.total && (
                                <span className="font-semibold text-[#1e3a5f]">
                                  £{approval.entity_summary.total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                </span>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>{approval.requester_name || approval.requested_by_name || 'Unknown'}</span>
                            </div>
                            <span>
                              {new Date(approval.created_at).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {approval.reviewer_name && (
                              <div className="flex items-center gap-1">
                                <CheckCircle2 className="w-4 h-4" />
                                <span>By {approval.reviewer_name}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Show reason if processed */}
                          {approval.status !== 'pending' && approval.decision_note && (
                            <div className={`mt-3 p-3 rounded-xl ${
                              approval.status === 'approved' ? 'bg-emerald-50' : 'bg-red-50'
                            }`}>
                              <p className="text-sm">
                                <span className="font-medium">Note:</span> {approval.decision_note}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      {isDirector && approval.status === 'pending' && (
                        <Button
                          onClick={() => openReview(approval)}
                          className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                          data-testid={`review-${approval.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Review
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-slate-700 mb-2">
                No {filters.status === 'all' ? '' : filters.status} requests
              </h3>
              <p className="text-slate-500">
                {isDirector 
                  ? 'All caught up! No pending approvals.'
                  : 'You have no approval requests.'}
              </p>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="mt-4 rounded-full"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Review Modal */}
        <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                Review Request
              </DialogTitle>
            </DialogHeader>
            
            {selectedApproval && (
              <div className="space-y-6 mt-4">
                {/* Request Info */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">Requested by</p>
                  <p className="font-medium text-slate-800">{selectedApproval.requested_by_name}</p>
                  <p className="text-sm text-slate-500 mt-2">
                    {new Date(selectedApproval.created_at).toLocaleString('en-GB')}
                  </p>
                </div>
                
                {/* Entity Details */}
                {entityData?.type === 'quote' && (
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-4">
                      <FileText className="w-5 h-5 text-[#1e3a5f]" />
                      <span className="font-medium">Quote Details</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Quote Number</p>
                        <p className="font-mono">{entityData.data.quote_number}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Total Value</p>
                        <p className="font-heading text-lg font-bold text-[#1e3a5f]">
                          £{entityData.data.total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Line Items</p>
                        <p>{entityData.data.line_items?.length || 0} items</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Validity</p>
                        <p>{entityData.data.validity_days} days</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {entityData?.type === 'decision' && (
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-4">
                      <Zap className="w-5 h-5 text-[#1e3a5f]" />
                      <span className="font-medium">Decision Details</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Current Outcome</p>
                        <p className="font-medium">{entityData.data.outcome_label}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Score</p>
                        <p>{entityData.data.total_score}/{entityData.data.max_score}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Decision Note - REQUIRED */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Decision Note
                    <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    value={decisionNote}
                    onChange={(e) => setDecisionNote(e.target.value)}
                    placeholder="Add your decision note (required)..."
                    className="min-h-[100px]"
                    data-testid="decision-note"
                  />
                  <p className="text-xs text-slate-500">
                    Required: Explain your decision for audit purposes
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowReviewModal(false)}
                    className="flex-1 rounded-full"
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => processApproval('reject')}
                    disabled={processing || decisionNote.length < 10}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                    data-testid="reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => processApproval('approve')}
                    disabled={processing || decisionNote.length < 5}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                    data-testid="approve-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
